import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import {IHeaderAngularComp} from "ag-grid-angular";
import {IHeaderParams} from "ag-grid-community";
import { focusPopoverDialog, trapPopoverDialogTab } from "../../../../common/utils/popover-dialog-a11y";

// Upper bound on the animation frames to wait for AG Grid to attach this
// header under its live `.ag-header-cell` (~0.5s at 60fps). Mirrors the
// plan-detail-clickable-header attach polling.
const MAX_ATTACH_FRAMES = 30;

@Component({
  standalone: false,
  selector: "wfm-esp-week-header-v2",
  templateUrl: "./week-header-v2.component.html",
  styleUrls: ["./week-header-v2.component.scss"]
})
export class WeekHeaderComponentV2 implements OnInit, AfterViewInit, OnDestroy, IHeaderAngularComp {


  params:IHeaderParams;

  @ViewChild("resetWeekBtn") resetWeekBtn: ElementRef<HTMLElement>;

  private headerCell: HTMLElement | null = null;
  private attachFrame: number | null = null;

  // a11y (ESP "Tab cannot move header->body"): the MU Dist grid no longer sets
  // suppressHeaderKeyboardEvent, so AG Grid natively walks the header cells and
  // hands Tab off into the body. AG Grid focuses the header CELL (tabindex="-1")
  // as you Tab across the row; forward that focus to the inner Reset-week
  // <button> so the button stays a keyboard-reachable, single Tab stop.
  private readonly forwardFocusToButton = (event: FocusEvent): void => {
    if (this.headerCell && event.target === this.headerCell) {
      const button = this.headerCell.querySelector<HTMLButtonElement>("button");
      if (button && !button.disabled) {
        button.focus();
      }
    }
  };

  constructor(@Inject(DOCUMENT) private document: Document, private readonly elementRef: ElementRef<HTMLElement>) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // AG Grid inserts this component into the live `.ag-header-cell` only AFTER
    // Angular's ngAfterViewInit, and not always within a single frame, so poll
    // across a bounded number of frames until the cell exists.
    this.attachToHeaderCell(MAX_ATTACH_FRAMES);
  }

  private attachToHeaderCell(remainingFrames: number): void {
    this.attachFrame = requestAnimationFrame(() => {
      const cell = this.elementRef.nativeElement.closest<HTMLElement>(".ag-header-cell");
      if (cell) {
        this.headerCell = cell;
        this.headerCell.addEventListener("focusin", this.forwardFocusToButton);
        return;
      }
      if (remainingFrames > 1) {
        this.attachToHeaderCell(remainingFrames - 1);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.attachFrame != null) {
      cancelAnimationFrame(this.attachFrame);
    }
    this.headerCell?.removeEventListener("focusin", this.forwardFocusToButton);
    this.headerCell = null;
  }

  agInit(params: IHeaderParams): void {
    this.params = params;
  }
  isResetAvailable(){
    if(this.params.context.isReadOnly()){
      return false;
    }

    let firstRow = this.params.api.getRowNode("0");
    const isEdited:boolean = firstRow && firstRow.data[this.params.column.getColDef().field]!==undefined;
    if(isEdited){
      return true;
    }
    return false;
  }
  resetColumn(){
    this.params.context.resetColumn(this.params.column);
  }

  isDisabled(){
    const isValidBase: boolean = this.params.context.isValidColumn("base");
    const isValidColumn = this.params.context.isValidColumn(this.params.column.getColDef().field);
    const isGridValid = this.params.context.isGridValid();

    const isEnabled = isGridValid || (isValidBase && isValidColumn==false);

    return !isEnabled;
  }

  // a11y (WCAG 2.1.1 Keyboard / 2.4.3 Focus Order): move focus into the
  // confirmation dialog when it opens so keyboard users land on Cancel/Confirm
  // instead of being left behind on the background controls.
  onConfirmShown(): void {
    setTimeout(() => focusPopoverDialog(this.document, "ngb-popover-window.reset-popup"));
  }

  // a11y (focus trap): keep Tab / Shift+Tab cycling between Cancel and Confirm.
  onConfirmDialogTab(event: KeyboardEvent): void {
    trapPopoverDialogTab(this.document, "ngb-popover-window.reset-popup", event);
  }

  // a11y: return focus to the trigger button when the dialog closes.
  onConfirmHidden(): void {
    setTimeout(() => this.resetWeekBtn?.nativeElement?.focus());
  }

  refresh(params: IHeaderParams): boolean {
    return false;
  }

}
